import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Modal,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { FormSection, SearchBar, AvatarCircle } from '@/components';
import { useLazyListMessageResidentsQuery, syncHookFormAsQuery } from '@/store/api/admin/messagesApi';
import { useAppSelector } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';

import { IMessageResident } from '@/types/admin/messages';

interface RecipientSelectorProps {
  recipients: IMessageResident[];
  onRecipientsChange: (recipients: IMessageResident[]) => void;
  error?: string;
  messageType: 'groupChat' | 'singleChat' | 'sms';
  isEditMode?: boolean;
  originalRecipients?: IMessageResident[];
}

export const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  recipients,
  onRecipientsChange,
  error,
  messageType,
  isEditMode = false,
  originalRecipients = [],
}) => {
  const { redesign, semantic, admin } = useAdminColors();
  const { layoutMode } = useResponsive();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageNum, setPageNum] = useState(0);
  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  const [getResidents, { data, isLoading }] = useLazyListMessageResidentsQuery();
  const propertyId = useAppSelector((state) => state.adminUser.selectedPropertyId);

  // Fetch residents when search query changes
  useEffect(() => {
    if (!propertyId) return;
    const q = debouncedSearch.trim();
    if (q.length === 0) return;
    const searchParams = syncHookFormAsQuery({
      propertyId: propertyId.toString(),
      pageNum: pageNum.toString(),
      searchQuery: q,
    });
    getResidents(searchParams);
  }, [propertyId, debouncedSearch, getResidents, pageNum]);

  // Add recipient
  const addRecipient = useCallback((recipient: IMessageResident) => {
    if (!recipients.find((r: IMessageResident) => r.id === recipient.id)) {
      const newRecipients = messageType === 'singleChat' ? [recipient] : [...recipients, recipient];
      onRecipientsChange(newRecipients);
    }
    setShowModal(false);
    setSearchQuery('');
  }, [recipients, messageType, onRecipientsChange]);

  // Add multiple recipients (for units)
  const addMultipleRecipients = useCallback((newRecipients: IMessageResident[]) => {
    const filteredRecipients = newRecipients.filter(
      (r: IMessageResident) => !recipients.find((recipient: IMessageResident) => recipient.id === r.id)
    );
    onRecipientsChange([...recipients, ...filteredRecipients]);
    setShowModal(false);
    setSearchQuery('');
  }, [recipients, onRecipientsChange]);

  // Remove recipient (only allow removing new recipients in edit mode)
  const removeRecipient = useCallback((recipient: IMessageResident) => {
    // In edit mode, don't allow removing original recipients
    if (isEditMode && originalRecipients.some((orig: IMessageResident) => orig.id === recipient.id)) {
      return;
    }
    const newRecipients = recipients.filter((r: IMessageResident) => r.id !== recipient.id);
    onRecipientsChange(newRecipients);
  }, [recipients, onRecipientsChange, isEditMode, originalRecipients]);

  // Open recipient selector modal/panel
  const openSelector = useCallback(() => {
    setShowModal(true);
    setSearchQuery('');
    setPageNum(0);
  }, []);

  // Close modal/panel
  const closeModal = useCallback(() => {
    setShowModal(false);
    setSearchQuery('');
  }, []);

  // Render selected recipient
  const renderSelectedRecipient = ({ item }: { item: IMessageResident }) => {
    const leaseCustomer: any = (item?.leaseCustomers && item.leaseCustomers[0]) || {};
    const hasName = Boolean(leaseCustomer?.first_name && leaseCustomer?.last_name);
    const displayName = hasName
      ? `${leaseCustomer.first_name} ${leaseCustomer.last_name}`
      : leaseCustomer?.email || 'Unknown Resident';

    const statusBase = (item?.statusType || '').toString();
    const statusLabel = statusBase
      ? `${statusBase.charAt(0).toUpperCase()}${statusBase.slice(1)}`
      : 'Resident';
    const role = leaseCustomer?.customerType === 'primary' ? 'resident' : 'guarantor';
    const unit = item?.unitNumberSpace || '';
    const statusText = unit ? `${statusLabel} ${role} - ${unit}` : `${statusLabel} ${role}`;

    const isOriginalRecipient = isEditMode && originalRecipients.some((orig: IMessageResident) => orig.id === item.id);
    const canRemove = !isOriginalRecipient;

    return (
      <View style={styles.selectedRecipientWrap}>
        <GlassCard padding={12}>
          <View style={styles.selectedRecipientRow}>
            <AvatarCircle
              firstName={leaseCustomer?.first_name}
              lastName={leaseCustomer?.last_name}
              size={40}
              style={styles.avatar}
            />

            <View style={styles.recipientInfo}>
              <Text style={[styles.recipientName, { color: redesign.text.primary }]}>
                {displayName}
              </Text>
              <Text style={[styles.recipientStatus, { color: redesign.text.muted }]}>
                {statusText}
              </Text>
              {isOriginalRecipient && (
                <View style={styles.originalBadge}>
                  <GlassStatusBadge label="Original" variant="info" />
                </View>
              )}
            </View>

            {canRemove && (
              <TouchableOpacity
                style={styles.removeButton}
                onPress={() => removeRecipient(item)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <SVG_COMPONENTS.CANCEL_X
                  width={16}
                  height={16}
                  color={redesign.text.muted}
                />
              </TouchableOpacity>
            )}
          </View>
        </GlassCard>
      </View>
    );
  };

  // Render available recipient in modal/panel
  const renderAvailableRecipient = ({ item }: { item: IMessageResident }) => {
    if (item.isUnit) {
      return (
        <TouchableOpacity
          style={styles.availableRecipientWrap}
          onPress={() => addMultipleRecipients(item.accounts || [])}
        >
          <GlassCard padding={16}>
            <View style={styles.availableRecipientRow}>
              <View
                style={[
                  styles.unitIcon,
                  { backgroundColor: redesign.card.bg },
                ]}
              >
                {Platform.OS === 'web' ? (
                  <SVG_COMPONENTS.PROPERTY
                    width={20}
                    height={20}
                    color={redesign.text.muted}
                  />
                ) : (
                  <SVG_COMPONENTS.NEW_PROPERTY
                    width={20}
                    height={20}
                    color={redesign.text.muted}
                  />
                )}
              </View>

              <View style={styles.recipientInfo}>
                <Text style={[styles.recipientName, { color: redesign.text.primary }]}>
                  {item.unitNumberSpace || `Unit ${item.unitNumber || ''}`}
                </Text>
                <Text style={[styles.recipientStatus, { color: redesign.text.muted }]}>
                  {item.accounts?.length || 0} residents
                </Text>
              </View>
            </View>
          </GlassCard>
        </TouchableOpacity>
      );
    }

    const leaseCustomer: any = (item?.leaseCustomers && item.leaseCustomers[0]) || {};
    const hasName = Boolean(leaseCustomer?.first_name && leaseCustomer?.last_name);
    const displayName = hasName
      ? `${leaseCustomer.first_name} ${leaseCustomer.last_name}`
      : leaseCustomer?.email || 'Unknown Resident';

    const statusBase = (item?.statusType || '').toString();
    const statusLabel = statusBase
      ? `${statusBase.charAt(0).toUpperCase()}${statusBase.slice(1)}`
      : 'Resident';
    const role = leaseCustomer?.customerType === 'primary' ? 'resident' : 'guarantor';
    const unit = item?.unitNumberSpace || '';
    const statusText = unit ? `${statusLabel} ${role} - ${unit}` : `${statusLabel} ${role}`;

    return (
      <TouchableOpacity
        style={styles.availableRecipientWrap}
        onPress={() => addRecipient(item)}
      >
        <GlassCard padding={16}>
          <View style={styles.availableRecipientRow}>
            <AvatarCircle
              firstName={leaseCustomer.first_name}
              lastName={leaseCustomer.last_name}
              size={40}
              style={styles.avatar}
            />

            <View style={styles.recipientInfo}>
              <Text style={[styles.recipientName, { color: redesign.text.primary }]}>
                {displayName}
              </Text>
              <Text style={[styles.recipientStatus, { color: redesign.text.muted }]}>
                {statusText}
              </Text>
            </View>
          </View>
        </GlassCard>
      </TouchableOpacity>
    );
  };

  const availableRecipients = data?.response || [];
  const singleLeaseRecipients = availableRecipients.filter((item: IMessageResident) => !item.isUnit);
  const unitRecipients = availableRecipients.filter((item: IMessageResident) => item.isUnit);
  const allAvailableRecipients = [...unitRecipients, ...singleLeaseRecipients];

  const renderSelectorBody = () => (
    <View style={{ flex: 1, backgroundColor: redesign.bg.base }}>
      {layoutMode === 'desktop' ? (
        <DesktopPanelHeader title="Select Recipients" onClose={closeModal} />
      ) : (
        <View
          style={[
            styles.modalHeader,
            {
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.modalBackButton}
            onPress={closeModal}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <Text style={[styles.modalTitle, { color: redesign.text.primary }]}>
            Select Recipients
          </Text>

          <View style={styles.modalSpacer} />
        </View>
      )}

      <View style={styles.modalContent}>
        <SearchBar
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search residents..."
          style={styles.searchBar}
        />

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={redesign.accent.blueBg} />
            <Text style={[styles.loadingText, { color: redesign.text.muted }]}>
              Searching residents...
            </Text>
          </View>
        ) : searchQuery.length === 0 ? (
          <View style={styles.emptyState}>
            <SVG_COMPONENTS.SELECT_RESIDENT
              width={48}
              height={48}
              color={redesign.text.muted}
            />
            <Text style={[styles.emptyStateText, { color: redesign.text.muted }]}>
              Start typing to search for residents
            </Text>
          </View>
        ) : allAvailableRecipients.length === 0 ? (
          <View style={styles.emptyState}>
            <SVG_COMPONENTS.GROUP
              width={48}
              height={48}
              color={redesign.text.muted}
            />
            <Text style={[styles.emptyStateText, { color: redesign.text.muted }]}>
              No residents found for &quot;{searchQuery}&quot;
            </Text>
          </View>
        ) : (
          <FlatList
            data={allAvailableRecipients}
            renderItem={renderAvailableRecipient}
            keyExtractor={(item) => item.isUnit ? `unit-${item.unitNumberSpace || item.unitNumber || 'unknown'}` : String(item.id)}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.recipientsList}
          />
        )}
      </View>
    </View>
  );

  return (
    <FormSection title="Recipients">
      <View style={styles.container}>
        <Text style={[styles.subtitle, { color: redesign.text.muted }]}>
          {isEditMode
            ? 'Add new recipients to this conversation. Original recipients cannot be removed.'
            : 'Select residents who will receive this message'
          }
        </Text>

        {error && (
          <Text style={[styles.errorText, { color: semantic.status.error }]}>
            {error}
          </Text>
        )}

        {/* Add Recipients Button */}
        <TouchableOpacity onPress={openSelector} activeOpacity={0.8}>
          <GlassCard padding={16} dashed style={styles.addButtonCard}>
            <View style={styles.addButtonRow}>
              <SVG_COMPONENTS.ADD_BUTTON
                width={20}
                height={20}
                color={admin.formInput.borderFocused}
              />
              <Text style={[styles.addButtonText, { color: admin.formInput.borderFocused }]}>
                {isEditMode ? 'Add New Recipients' : 'Add Recipients'}
              </Text>
            </View>
          </GlassCard>
        </TouchableOpacity>

        {/* Selected Recipients List */}
        {recipients.length > 0 && (
          <View style={styles.selectedRecipientsContainer}>
            <Text style={[styles.selectedRecipientsTitle, { color: redesign.text.primary }]}>
              {isEditMode ? 'Recipients' : 'Selected Recipients'} ({recipients.length})
            </Text>
            <FlatList
              data={recipients}
              renderItem={renderSelectedRecipient}
              keyExtractor={(item) => item.id.toString()}
              showsVerticalScrollIndicator={false}
              scrollEnabled={false}
            />
          </View>
        )}

        {/* Recipient Selector - desktop side panel or mobile modal */}
        {layoutMode === 'desktop' ? (
          <DesktopSidePanel visible={showModal} onClose={closeModal} width={520}>
            {renderSelectorBody()}
          </DesktopSidePanel>
        ) : (
          <Modal
            visible={showModal}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={closeModal}
          >
            {renderSelectorBody()}
          </Modal>
        )}
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  errorText: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  addButtonCard: {
    borderStyle: 'dashed',
  },
  addButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  selectedRecipientsContainer: {
    marginTop: 16,
  },
  selectedRecipientsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  selectedRecipientWrap: {
    marginBottom: 8,
  },
  selectedRecipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  availableRecipientWrap: {
    marginBottom: 8,
  },
  availableRecipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    marginRight: 12,
  },
  unitIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  recipientInfo: {
    flex: 1,
  },
  recipientName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  recipientStatus: {
    fontSize: 14,
  },
  originalBadge: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  removeButton: {
    padding: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    paddingTop: 50,
  },
  modalBackButton: {
    padding: 8,
    marginRight: 8,
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  modalSpacer: {
    width: 40,
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: 16,
  },
  searchBar: {
    marginVertical: 16,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    lineHeight: 22,
  },
  recipientsList: {
    paddingBottom: 16,
  },
});
